// types_deck.ts — Types et constantes du panneau de contrôle (deck / keymap).
// v2.26 — passage à 12 slots + ajout rangée layout

import { SLOT_COUNT, SLOT_COUNT_PIP } from '../../contexts/MediaStreamContext';

export interface EventBinding {
  id:          string;
  type:        'keyboard' | 'mouse' | 'gamepad';
  event:       string;
  action:      'alert' | 'prevent';
  description: string;
}

export interface KeyBinding {
  code:     string;   // e.code physique : 'Digit1', 'Numpad1', 'F1', 'KeyA'…
  display?: string;   // e.key au moment de la capture → affichage layout-aware (ex: 'q' sur AZERTY)
  ctrl:     boolean;
  shift:    boolean;
  alt:      boolean;
}

// Affectation d'un bouton média du deck (indépendant du type)
export type MediaKind = 'image' | 'sound' | 'jingle';

export interface MediaAssignment {
  kind:   MediaKind;
  slotId: string;
}

export interface DeckKeyMap {
  video:   (KeyBinding | null)[];  // 12 slots sources vidéo
  preview: (KeyBinding | null)[];  // 12 slots preview source
  cut:     (KeyBinding | null)[];  // 1 slot bouton CUT (tableau de 1)
  image:   (KeyBinding | null)[];  // 12 slots images overlay
  sound:   (KeyBinding | null)[];  // 12 slots sons
  pip:     (KeyBinding | null)[];  // 12 slots PiP vidéo
  jingle:  (KeyBinding | null)[];  // 12 slots jingles vidéo
  layout:  (KeyBinding | null)[];  // 12 slots layouts
}

export const DEFAULT_KEY_MAP: DeckKeyMap = {
  video: [
    { code: 'Digit1', ctrl: false, shift: false, alt: false },
    { code: 'Digit2', ctrl: false, shift: false, alt: false },
    { code: 'Digit3', ctrl: false, shift: false, alt: false },
    { code: 'Digit4', ctrl: false, shift: false, alt: false },
    { code: 'Digit5', ctrl: false, shift: false, alt: false },
    { code: 'Digit6', ctrl: false, shift: false, alt: false },
    { code: 'Digit7', ctrl: false, shift: false, alt: false },
    { code: 'Digit8', ctrl: false, shift: false, alt: false },
    { code: 'Digit9', ctrl: false, shift: false, alt: false },
    { code: 'Digit0', ctrl: false, shift: false, alt: false },
    null,
    null,
  ],
  preview: [
    { code: 'Digit1', ctrl: false, shift: false, alt: true },
    { code: 'Digit2', ctrl: false, shift: false, alt: true },
    { code: 'Digit3', ctrl: false, shift: false, alt: true },
    { code: 'Digit4', ctrl: false, shift: false, alt: true },
    { code: 'Digit5', ctrl: false, shift: false, alt: true },
    { code: 'Digit6', ctrl: false, shift: false, alt: true },
    { code: 'Digit7', ctrl: false, shift: false, alt: true },
    { code: 'Digit8', ctrl: false, shift: false, alt: true },
    { code: 'Digit9', ctrl: false, shift: false, alt: true },
    { code: 'Digit0', ctrl: false, shift: false, alt: true },
    null,
    null,
  ],
  cut: [
    { code: 'Space', ctrl: false, shift: false, alt: false },
  ],
  image: [
    { code: 'F1',  ctrl: false, shift: false, alt: false },
    { code: 'F2',  ctrl: false, shift: false, alt: false },
    { code: 'F3',  ctrl: false, shift: false, alt: false },
    { code: 'F4',  ctrl: false, shift: false, alt: false },
    { code: 'F5',  ctrl: false, shift: false, alt: false },
    { code: 'F6',  ctrl: false, shift: false, alt: false },
    { code: 'F7',  ctrl: false, shift: false, alt: false },
    { code: 'F8',  ctrl: false, shift: false, alt: false },
    { code: 'F9',  ctrl: false, shift: false, alt: false },
    { code: 'F10', ctrl: false, shift: false, alt: false },
    { code: 'F11', ctrl: false, shift: false, alt: false },
    { code: 'F12', ctrl: false, shift: false, alt: false },
  ],
  sound: [
    null, null, null, null, null, null, null, null, null, null, null, null,
  ],
  pip: [
    null, null, null, null, null, null, null, null, null, null, null, null,
  ],
  jingle: [
    null, null, null, null, null, null, null, null, null, null, null, null,
  ],
  layout: [
    null, null, null, null, null, null, null, null, null, null, null, null,
  ],
};

export interface RowConfig {
  label:  string;
  bg:     string;
  border: string;
  text:   string;
  badge:  string;
  count:  number;
}

export const ROW_CONFIG: Record<keyof DeckKeyMap, RowConfig> = {
  video:   { label: '🎬 Sources vidéo',  bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.3)',   text: '#fca5a5', badge: 'rgba(127,29,29,0.6)',  count: SLOT_COUNT },
  preview: { label: '👁 Preview source', bg: 'rgba(56,189,248,0.08)',  border: 'rgba(56,189,248,0.3)',  text: '#7dd3fc', badge: 'rgba(12,74,110,0.6)',  count: SLOT_COUNT },
  cut:     { label: '✂️ CUT',            bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.3)',   text: '#fca5a5', badge: 'rgba(127,29,29,0.6)',  count: 1 },
  image:   { label: '🖼 Images overlay', bg: 'rgba(99,102,241,0.08)',  border: 'rgba(99,102,241,0.3)',  text: '#a5b4fc', badge: 'rgba(30,27,75,0.6)',   count: SLOT_COUNT },
  sound:   { label: '🔊 Sons',           bg: 'rgba(34,211,238,0.08)',  border: 'rgba(34,211,238,0.3)',  text: '#67e8f9', badge: 'rgba(12,74,110,0.6)',  count: SLOT_COUNT },
  pip:     { label: '⧉ PiP vidéo',      bg: 'rgba(232,121,249,0.08)', border: 'rgba(232,121,249,0.3)', text: '#f0abfc', badge: 'rgba(88,28,135,0.6)', count: SLOT_COUNT_PIP },
  jingle:  { label: '🎞 Jingles vidéo', bg: 'rgba(251,146,60,0.08)',  border: 'rgba(251,146,60,0.3)',  text: '#fdba74', badge: 'rgba(124,45,18,0.6)', count: SLOT_COUNT },
  layout:  { label: '📐 Scènes',        bg: 'rgba(96,165,250,0.08)',  border: 'rgba(96,165,250,0.3)',  text: '#93c5fd', badge: 'rgba(30,58,138,0.6)', count: SLOT_COUNT },
};

export const KEYMAP_STORAGE_KEY   = 'mediartc_deck_keymap';
export const EXTERNAL_SOURCES_KEY = 'mediartc_external_sources';
